import { TEAM } from "@/data/company";

const TEAM_IMG =
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxnbG9iYWwlMjBidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nfGVufDB8fHx8MTc4MjQ1NDk5MHww&ixlib=rb-4.1.0&q=85";

function initials(name) {
  return name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("");
}

export default function Team() {
  const leader = TEAM[0];
  const rest = TEAM.slice(1);

  return (
    <div data-testid="page-team">
      {/* HEADER */}
      <section className="pt-28 lg:pt-36 pb-16 lg:pb-24 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <div className="overline">Leadership</div>
            <h1
              data-testid="team-title"
              className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]"
            >
              An operator-led team.
              <br />
              Global by design.
            </h1>
          </div>
          <div className="lg:col-span-6">
            <p className="text-slate-700 text-lg leading-relaxed">
              Our investment principals and sector partners combine
              institutional banking experience with operating depth across
              healthcare, fintech, real estate, agriculture, and clean energy.
            </p>
            <p className="mt-6 text-slate-500 leading-relaxed">
              We work as one team across BVI and Singapore — supported by
              regional partners on the ground in each market we serve.
            </p>
          </div>
        </div>
      </section>

      {/* FEATURED LEADER */}
      <section
        data-testid="featured-leader"
        className="bg-white border-y border-slate-200"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] w-full overflow-hidden border border-slate-200 bg-slate-100 relative">
                {leader.image ? (
                  <img
                    src={leader.image}
                    alt={leader.name}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center font-serif text-7xl text-slate-400">
                    {initials(leader.name)}
                  </div>
                )}
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="overline">Office of the Managing Director</div>
              <h2 className="mt-5 font-serif text-4xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]">
                {leader.name}
              </h2>
              <div className="mt-3 text-slate-500 text-sm tracking-[0.18em] uppercase font-semibold">
                {leader.title}
              </div>
              <p className="mt-8 text-slate-700 text-lg leading-relaxed">
                {leader.bio}
              </p>

              <div className="mt-10 grid grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                {[
                  { k: "20+", v: "Years of experience" },
                  { k: "8+", v: "Geographies overseen" },
                  { k: "5", v: "Sectors of focus" },
                ].map((s) => (
                  <div key={s.v} className="bg-white p-5">
                    <div className="font-serif text-2xl lg:text-3xl text-slate-900">
                      {s.k}
                    </div>
                    <div className="overline mt-2 text-slate-500">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HERO IMAGE BAND */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16">
          <div className="aspect-[21/9] w-full overflow-hidden border border-slate-200">
            <img
              src={TEAM_IMG}
              alt="Global business team in discussion"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* TEAM GRID */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="overline">The team</div>
          <h2 className="mt-5 font-serif text-3xl lg:text-5xl tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Partners across every sector and geography we serve.
          </h2>

          <div
            data-testid="team-grid"
            className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200"
          >
            {rest.map((m, i) => (
              <article
                key={m.name + i}
                data-testid={`team-card-${i}`}
                className="bg-white p-8 lg:p-10 min-h-[300px] hover:bg-slate-50 transition-colors"
              >
                {m.image ? (
                  <div className="w-16 h-16 overflow-hidden border border-slate-300">
                    <img
                      src={m.image}
                      alt={m.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="w-14 h-14 border border-slate-300 flex items-center justify-center font-serif text-xl text-slate-700">
                    {initials(m.name)}
                  </div>
                )}
                <h3 className="mt-6 font-serif text-2xl tracking-tight text-slate-900">
                  {m.name}
                </h3>
                <div className="mt-1 overline text-slate-500">{m.title}</div>
                <p className="mt-5 text-sm text-slate-600 leading-relaxed">
                  {m.bio}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-20 lg:py-28 bg-slate-950 text-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline text-slate-400">Our approach</div>
            <h2 className="mt-5 font-serif text-3xl lg:text-5xl tracking-tight text-white leading-[1.05]">
              We hire operators,
              <br />
              not intermediaries.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-slate-300 text-lg leading-relaxed">
              Each sector lead has built or operated meaningful businesses
              within their domain. This shapes how we underwrite, how we
              partner, and how we exercise governance.
            </p>
            <p className="mt-6 text-slate-400 leading-relaxed">
              Our principals share equity in outcomes and a common standard:
              clarity, discretion, and long-term alignment.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
